import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, getDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../../firebaseConfig";
import ConfirmModal from "../modals/confirmModal";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "../ChatForm/SearchBar.css";
import { v4 as uuidv4 } from 'uuid';

import Agent0Icon from "../../assets/images/agent-0.svg?url";
import Agent1Icon from "../../assets/images/agent-1.svg?url";
import Agent2Icon from "../../assets/images/agent-2.svg?url";

function AgentSelector({ isOpen, onClose,  isNewAgentOpen=true, handleNewAgentToggle, closeModal, showActions=true, setCurrentAgent, onDataLoad="", setAvailableAgentsList }) {
  const [agents, setAgents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [agentToEdit, setAgentToEdit] = useState(null); // State for agent being edited
  const [newAgentName, setNewAgentName] = useState(""); // State for new agent name
  const [newAgentRole, setNewAgentRole] = useState(""); // State for new agent role
  const [newAgentInst, setNewAgentInst] = useState(""); // State for new agent instructions
  
  const agentIcons = [Agent0Icon, Agent1Icon, Agent2Icon];

  useEffect(() => {
    const fetchAgents = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found.");
        return;
      }
      const userUID = currentUser.uid;

      // Query the user's document directly
      const userDocRef = doc(db, "users", userUID);

      try {
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          const agentsData = userData.agents || []; // Get the agents array or an empty array if it doesn't exist
          // const currentAgentId = userData.agentCurrent; // No longer needed here for initial set

          setAgents(agentsData);
          if (setAvailableAgentsList) {
            setAvailableAgentsList(agentsData); // Pass the fetched agents to the parent
          }
          // GeminiComponent is now responsible for setting its initial agent after receiving this list.
        } else {
          console.log("No user document found.");
          setAgents([]);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    fetchAgents();
  // This useEffect should only depend on setAvailableAgentsList.
  // setCurrentAgent (the prop) should not be a dependency for the initial fetch logic.
  }, [setAvailableAgentsList]);
  
  // Effect to reset form when it's closed, ensuring a clean state for creation or next edit
  useEffect(() => {
    if (!isNewAgentOpen) {
      setAgentToEdit(null);
      setNewAgentName("");
      setNewAgentRole("");
      setNewAgentInst("");
    }
  }, [isNewAgentOpen]);

  const handleDelete = async () => {
    if (agentToDelete) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found.");
        return;
      }
      const userUID = currentUser.uid;
      const userDocRef = doc(db, "users", userUID); // Reference to the user's document

      try {
        const userDocSnapshot = await getDoc(userDocRef); // Get the user's document
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          const updatedAgents = userData.agents.filter((agent) => agent.id !== agentToDelete); // Filter out the agent to delete
          await updateDoc(userDocRef, { agents: updatedAgents }); // Update the user's document with the new array
          setAgents(updatedAgents); // Update the state
          console.log("Agent deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting agent:", error);
      }
      setIsModalOpen(false);
    }
  };

  const handleAgentItemClick = async (agent) => {
    if (agent) {
      // console.log("Agent selected:" + agent.name + " : "+ agent.inst);
      if (setCurrentAgent) { // This is the prop from GeminiComponent
        setCurrentAgent({ ...agent }); // Inform GeminiComponent of the user's selection
      }

      // Also save the selected agent's ID to Firebase
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userUID = currentUser.uid;
        const userDocRef = doc(db, "users", userUID);
        try {
          await updateDoc(userDocRef, { agentCurrent: agent.id });
          console.log("Saved current agent ID to user document:", agent.id);
        } catch (error) {
          console.error("Error saving current agent ID:", error);
        }
      }
      if (closeModal) {
        closeModal(); // Call the passed function to close the modal in GeminiComponent
      }
      if (showActions === false) {
        // onDataLoad(agent); // Pass the data up
      }
    }
  };

  const handleEditClick = (e, agent) => {
    e.stopPropagation(); // Prevent the agent selection click from firing
    setAgentToEdit(agent.id);
    setNewAgentName(agent.name);
    setNewAgentRole(agent.role);
    setNewAgentInst(agent.inst);
    handleNewAgentToggle(true); // Open the form for editing
  };

  const handleSaveAgentClick = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      return;
    }
    const userUID = currentUser.uid;
    const userDocRef = doc(db, "users", userUID);

    try {
      const userDocSnapshot = await getDoc(userDocRef);
      if (userDocSnapshot.exists()) {
        const userData = userDocSnapshot.data();
        let updatedAgents;

        if (agentToEdit) {
          // Editing an existing agent
          console.log("Updating agent...");
          updatedAgents = (userData.agents || []).map(agent =>
            agent.id === agentToEdit
              ? { ...agent, name: newAgentName, role: newAgentRole, inst: newAgentInst }
              : agent
          );
        } else {
          // Creating a new agent
          console.log("Saving new agent...");
          const newAgent = {
            id: uuidv4(), // Generate a unique ID
            name: newAgentName,
            role: newAgentRole,
            inst: newAgentInst,
          };
          updatedAgents = [...(userData.agents || []), newAgent];
        }

        await updateDoc(userDocRef, { agents: updatedAgents });
        setAgents(updatedAgents); // Update the state
        if (setAvailableAgentsList) {
          setAvailableAgentsList(updatedAgents); // Update parent component's list
        }

        // If the edited agent is the current agent, update it in the parent
        if (agentToEdit && setCurrentAgent) {
            const updatedAgent = updatedAgents.find(a => a.id === agentToEdit);
            setCurrentAgent(updatedAgent);
        }

        handleNewAgentToggle(false); // Close the modal
        console.log(`Agent ${agentToEdit ? 'updated' : 'saved'} successfully!`);
      }
    } catch (error) {
      console.error(`Error ${agentToEdit ? 'updating' : 'saving'} agent:`, error);
    }
  };

  return (

    <div >
     {isNewAgentOpen === true && (
        <div className="drop-list" style={{ width: "480px", padding: "12px"}} >
          <p className="overline">{agentToEdit ? "Edit agent name" : "Give your agent a name"}</p>
          <input
            className="input"
            type="text"
            placeholder="Summary Agent, Deep Researcher, Nico"
            value={newAgentName}
            onChange={(e) => setNewAgentName(e.target.value)}
          />

          <p className="overline">{agentToEdit ? "Edit job role" : "Define the job role"}</p>
          <input
            className="input"
            type="multiline"
            placeholder="You are a data analyst at a digital product company."
            value={newAgentRole}
            onChange={(e) => setNewAgentRole(e.target.value)}
          />

          <p className="overline">{agentToEdit ? "Edit instructions" : "Describe how to complete tasks"}</p>
          <textarea
            className="input"
            type="multiline"
            placeholder="Summarize insights based on consumer behavior"
            value={newAgentInst}
            onChange={(e) => setNewAgentInst(e.target.value)}
            style={{ height: '120px', resize: 'vertical' }} // Add inline style for height and allow vertical resize
            rows={8}
          />
        <button className="button" type="button" onClick={handleSaveAgentClick}>
          {agentToEdit ? "Update Agent" : "Save this Agent"}
        </button>
        </div>
      )}

       {isOpen === true && isNewAgentOpen === false && (
        

        <div className="drop-list" style={{ width: "480px", padding: "12px" }}>

        <ul style={{ listStyleType: "none", margin: "0", padding: "0" }}>
          
        {agents && agents.length > 0 ? (
          agents.map((agent, index) => (
            <li className="recentsList"  style={{ padding: "0"}} key={agent.id}>
              <button style={{ padding: "0 0 16px 16px"}} className="pageList" onClick={() => handleAgentItemClick(agent)}>
             
                <div style={{ marginLeft: "4px", flexDirection: "row", display: "flex", alignItems: "middle", justifyContent: "center" }}>
                {/* Agent icon on the left side */}
              
                 <div style={{ marginRight: "12px", maxWidth: "64px",  height: "72px",  flexDirection: "column", padding: "4px", borderRadius: "0 0 8px 8px", backgroundColor: "#84A3AA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {/* Use index % 3 to cycle through agentIcons */}
                <img src={agentIcons[index % agentIcons.length]} alt={`Agent ${index}`} style={{ width: "52px", height: "52px", borderRadius: "0px" }} />
              </div>
              {/* Agent name and role on the right side */}
              <div style={{ width: "96%"}}>
                <span style={{ textTransform: "uppercase", marginTop:"12px", marginBottom:"0px", fontSize: "10px", letterSpacing: "1px", opacity: "0.7" }}>{agent.role}</span>
                <h3 style={{ marginTop: "8px", marginBottom: "-6px", width: "96%"}}>{agent.name || agent.id} </h3>
                <br></br>
                  <p style={{ display: "-webkit-box", WebkitLineClamp: "2", WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {agent.inst?.length > 123 ? agent.inst.substring(0, 123) + "..." : agent.inst}
                  </p>
                </div>
                </div>
              <div className="library-item-actions" style={{ width: "72px"}}>
                {showActions && (
                  <button
                    onClick={(e) => handleEditClick(e, agent)}
                    className="delBtn"
                  >
                    <EditIcon />
                  </button>
                )}
                {showActions && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent the agent selection click from firing
                    setAgentToDelete(agent.id);
                    setIsModalOpen(true);
                  }}
                  className="delBtn"
                >
                  <DeleteIcon />
                </button>
              )}
              </div>
              </button>
              
            </li>
          ))
        ) : (
          <li>Loading agents...</li>
        )}
      </ul>

          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "10px" }}>
            <button
              className="resFooterButton"
              onClick={() => handleNewAgentToggle(true)}
              style={{ margin: "0px", width: "48px", height: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
            </button>
          </div>

      </div>
      
          )}


      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
      />

    </div>
  );
}

export default AgentSelector;
